import { createApi } from "../../../utils/createApi";
import { markSiteUnsynced } from "../../../utils/syncStore";
import { AxiosError, AxiosResponse } from "axios";
import { NavigateFunction } from "react-router-dom";
import { universalAPIErrorHandler } from "../../../utils/universalAPIErrorHandler";
import {
  getNumbersAfterEditCamera,
  getNumbersAfterSites,
} from "../../EditCamera/EditCamera";
import { buildPresetZonePatchBodyFromZones } from "../../Sites/SiteAboutPage/FlexIgnoreZonesOnboarding/flexIgnoreZonesOnboardingPayload";
import { useFlexIgnoreZonesOnboardingStore } from "../../Sites/SiteAboutPage/FlexIgnoreZonesOnboarding/flexIgnoreZonesOnboardingStore";
import { useSiteAboutStore } from "../../Sites/SiteAboutPage/siteAboutStore/siteAboutStore";
import { MetricSimplifiedRow } from "../../Sites/SiteAboutPage/SiteAboutPageUtils/bulkUpdateCamerasApi";
import { IgnoreZone } from "../../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import {
  assignZonesToPresetZoneRowIds,
  extractPresetZoneRowIdsFromApiResponse,
} from "./flexPresetZoneRowResolve";
import { trimmedPresetNameFromBaselineSnapshot } from "./flexEditIgnoreZonesUtils";

const resolveZoneGroupsWithFallback = async (
  AxiosUI: ReturnType<typeof createApi>,
  cameraId: number,
  presetId: number,
  zones: IgnoreZone[]
): Promise<Map<number, IgnoreZone[]>> => {
  const res = await AxiosUI.get(
    `preset-zone/?camera=${cameraId}&preset=${presetId}`
  );
  const freshIds = extractPresetZoneRowIdsFromApiResponse(res.data);
  if (freshIds.length === 0) {
    throw new Error(
      `No preset-zone rows for camera ${cameraId} preset ${presetId}.`
    );
  }
  return assignZonesToPresetZoneRowIds(zones, freshIds);
};

const ensureMetricRows = async (
  customerIdNum: number
): Promise<MetricSimplifiedRow[]> => {
  const siteStore = useSiteAboutStore.getState();
  let metricRows =
    siteStore.siteMetricsSimplifiedCustomerId === customerIdNum
      ? siteStore.siteMetricsSimplified
      : [];
  if (metricRows.length === 0) {
    const AxiosUI = createApi("");
    const metricSimplifiedResponse = await AxiosUI.get("metric/simplified/", {
      params: { customer: customerIdNum },
    });
    if (Array.isArray(metricSimplifiedResponse.data?.results)) {
      metricRows = metricSimplifiedResponse.data.results;
    } else if (Array.isArray(metricSimplifiedResponse.data)) {
      metricRows = metricSimplifiedResponse.data;
    }
    siteStore.setSiteMetricsSimplifiedData(metricRows, customerIdNum);
  }
  return metricRows;
};

export const handleSubmitFlexIgnoreZones = async ({
  cameraId,
  customerId,
  dirtyPresetLocalIds,
  flexZonesBaseline,
  navigate,
  setPageLoading,
}: {
  cameraId: number;
  customerId: number;
  dirtyPresetLocalIds: string[];
  /** When set, used to detect display-name changes for `PATCH preset/{id}/`. */
  flexZonesBaseline?: Record<string, string>;
  navigate: NavigateFunction;
  setPageLoading: (v: boolean) => void;
}) => {
  const retryFunction = () => {
    handleSubmitFlexIgnoreZones({
      cameraId,
      customerId,
      dirtyPresetLocalIds,
      flexZonesBaseline,
      navigate,
      setPageLoading,
    });
  };
  const AxiosUI = createApi("");
  setPageLoading(true);

  let metricRows: MetricSimplifiedRow[] = [];
  try {
    metricRows = await ensureMetricRows(customerId);
  } catch (reason) {
    setPageLoading(false);
    universalAPIErrorHandler(reason as AxiosError, navigate, retryFunction, () => {
      alert("Unable to load metrics for saving flex ignore zones.");
    });
    return;
  }

  for (const localId of dirtyPresetLocalIds) {
    const state = useFlexIgnoreZonesOnboardingStore.getState();
    const preset = (state.presetsByCamera[cameraId] || []).find(
      (p) => p.localId === localId
    );
    if (!preset) {
      continue;
    }
    if (preset.persistedId == null) {
      const persistResult = await state.persistFlexPresetDraftToApi(
        cameraId,
        localId
      );
      if (!persistResult.ok) {
        setPageLoading(false);
        alert(persistResult.error);
        return;
      }
      continue;
    }

    const presetId = preset.persistedId;
    const trimmedName = (preset.name || "").trim();
    const baselineTrimmedName = flexZonesBaseline
      ? trimmedPresetNameFromBaselineSnapshot(flexZonesBaseline[localId])
      : undefined;
    if (baselineTrimmedName !== undefined && baselineTrimmedName !== trimmedName) {
      try {
        await AxiosUI.patch(`preset/${presetId}/`, { name: trimmedName });
      } catch (reason) {
        setPageLoading(false);
        universalAPIErrorHandler(reason as AxiosError, navigate, retryFunction, () => {
          alert(
            (reason as Error)?.message ||
              "Error updating flex preset name, please try again"
          );
        });
        return;
      }
    }
    const apiName = `${trimmedName} #${presetId}`;
    try {
      const groups = await resolveZoneGroupsWithFallback(
        AxiosUI,
        cameraId,
        presetId,
        preset.zones || []
      );
      for (const [zoneRowId, rowZones] of groups) {
        const body = buildPresetZonePatchBodyFromZones({
          cameraId,
          presetId,
          presetApiName: apiName,
          zones: rowZones,
          metricRows,
        });
        await AxiosUI.patch(`preset-zone/${zoneRowId}/`, body);
      }
    } catch (reason) {
      setPageLoading(false);
      universalAPIErrorHandler(reason as AxiosError, navigate, retryFunction, () => {
        alert(
          (reason as Error)?.message ||
            "Error submitting flex preset zone changes, please try again"
        );
      });
      return;
    }
  }

  // Saving no longer auto-syncs. Mark the site as having unsynced changes;
  // the operator syncs explicitly from the site About page.
  markSiteUnsynced();

  try {
    await useFlexIgnoreZonesOnboardingStore
      .getState()
      .loadCameraContext(cameraId, true);
  } catch {
    /* best-effort: re-fetch preset-zone rows so Default preset UI matches API */
  }

  if (location.href.includes("editignorezones")) {
    await AxiosUI.get(`camera/site/?customer__id=${customerId}`).then(
      (response: AxiosResponse) => {
        localStorage.setItem("siteCameras", JSON.stringify(response.data));
        navigate(
          `/sites/${Number(getNumbersAfterSites(location.href))}/editcamera/${Number(
            getNumbersAfterEditCamera(location.href)
          )}`
        );
      },
      (reason: AxiosError) => {
        universalAPIErrorHandler(reason, navigate, retryFunction, () => {});
      }
    );
  }
  setPageLoading(false);
};
